// frontend-react/src/services/activateWhenFull.js
//
// DD-DL-DEADLOCK-0726-FE — "activate when full": which teammate must move so a DL player can be
// activated, and the plan that does it in one step.
//
// THE INCIDENT. Deadwood sat at 23/23 active with Vinnie Pasquantino (1B) DL-locked under
// §4.5.15.1. The owner tried four times to move Brandon Nimmo — twice via /drop-player, twice via
// /move-player — and was correctly refused each time: a single move on a NON-flagged player
// resolves nothing, so the lock forbids it. The backend permits the resolution only when the
// freeing move and the activation arrive as ONE plan (roster_plan_executor.execute_plan passes
// every step's player_id to the DL gate).
//
// AND HE WAS TRYING THE WRONG PLAYER. Nimmo holds OF_2. Pasquantino is a 1B — he cannot occupy an
// OF slot, so benching Nimmo would not have enabled the activation even as part of a plan. The
// owner had no way to know that. THAT is what this module fixes: it names the teammates whose
// slots the flagged player can actually take.
//
// ELIGIBILITY IS CANONICAL, NOT A POSITION STRING. `isEligibleForSlot` mirrors the backend's
// core.position_utils.is_eligible_for_slot: slot TYPE is parsed off the slot id ("UTIL_0" -> UTIL)
// and matched against the player's effective eligibility. A raw position-string comparison would
// wrongly offer OF_2 to a first baseman, and a raw slot-COUNT check would call Acunalingus
// unblocked at 22/23 when its only open slot is P_7 and Soderstrom is an outfielder.
//
// DESIGN INTENT (owner-ratified): bench placement is a FAILSAFE in this league, not routine roster
// management. This affordance is the one moment it matters — a DL-locked team at capacity — so it
// surfaces there and nowhere else. It is deliberately not a general bench-management feature.

/** Default slot eligibility by MLB position — mirrors backend get_default_eligibility. */
export const DEFAULT_ELIGIBILITY = {
  C: ['C', 'UTIL'],
  '1B': ['1B', 'CI', 'UTIL'],
  '2B': ['2B', 'MI', 'UTIL'],
  '3B': ['3B', 'CI', 'UTIL'],
  SS: ['SS', 'MI', 'UTIL'],
  OF: ['OF', 'UTIL'],
  DH: ['UTIL'],
  P: ['P'],
  SP: ['P'],
  RP: ['P'],
};

/** "UTIL_0" -> "UTIL". Slot TYPE is what eligibility is expressed in. */
export function slotTypeOf(slot) {
  if (!slot) return null;
  return String(slot).includes('_') ? String(slot).split('_')[0] : String(slot);
}

/**
 * Canonical eligibility test. Mirrors backend `is_eligible_for_slot(slot, mlb_position, custom)`.
 * BENCH accepts anyone; everything else is matched on slot TYPE.
 */
export function isEligibleForSlot(slot, mlbPosition, customEligibility = null) {
  if (!slot || slot === 'BENCH' || slot === 'bench') return true;
  const type = slotTypeOf(slot);
  const eligible = (customEligibility && customEligibility.length)
    ? customEligibility
    : (DEFAULT_ELIGIBILITY[String(mlbPosition || '').toUpperCase()] || []);
  return eligible.includes(type);
}

/** roster entries whose status is 'active' and that hold a real slot. */
function activeOccupants(roster) {
  return (roster || [])
    .filter((p) => (p?.league_context?.roster?.status) === 'active')
    .map((p) => ({
      player: p,
      slot: p?.league_context?.roster?.position || null,
      name: [p?.info?.first_name, p?.info?.last_name].filter(Boolean).join(' ')
        || p?.player_name || 'Unknown',
      leaguePlayerId: p?.ids?.league_player,
      playerId: p?.ids?.mlb,
    }))
    .filter((o) => o.slot);
}

/**
 * Is there an OPEN slot the flagged player could take right now?
 * Requires the league's position_requirements so we know each slot type's capacity.
 * Returns the first open eligible slot id, or null.
 */
export function findOpenEligibleSlot(roster, flaggedPosition, positionRequirements,
                                     effectiveEligibility = null) {
  const occupied = new Set(activeOccupants(roster).map((o) => o.slot));
  const reqs = positionRequirements || {};
  for (const [type, cfg] of Object.entries(reqs)) {
    const slots = (cfg && cfg.slots) || 0;
    if (!isEligibleForSlot(`${type}_0`, flaggedPosition, effectiveEligibility)) continue;
    for (let i = 0; i < slots; i += 1) {
      const id = `${type}_${i}`;
      if (!occupied.has(id)) return id;
    }
  }
  return null;
}

/**
 * The teammates who could be benched to make room — i.e. the current occupants of slots the
 * flagged player is eligible for. THIS is the list the owner needs; guessing produced four
 * refused attempts on a player whose slot was never usable.
 *
 * @returns {Array<{leaguePlayerId, playerId, name, slot}>} sorted by slot for stable display
 */
export function eligibleSlotMates(roster, flaggedPosition, effectiveEligibility = null) {
  // `effectiveEligibility` is the CANONICAL list from getEffectiveEligibility(player) — it carries
  // composite-slot expansion and auto-earned eligibility that a bare position string does not.
  // Callers must pass it; the position string is only the fallback when it is unavailable.
  return activeOccupants(roster)
    .filter((o) => isEligibleForSlot(o.slot, flaggedPosition, effectiveEligibility))
    .sort((a, b) => String(a.slot).localeCompare(String(b.slot)));
}

/**
 * Does this team need the affordance at all?
 * True only when the flagged player has NO open eligible slot but DOES have eligible slot-mates.
 */
export function needsActivateWhenFull(roster, flaggedPosition, positionRequirements,
                                      effectiveEligibility = null) {
  if (findOpenEligibleSlot(roster, flaggedPosition, positionRequirements, effectiveEligibility)) {
    return false;
  }
  return eligibleSlotMates(roster, flaggedPosition, effectiveEligibility).length > 0;
}

/**
 * Build the two-step plan the backend already validates and permits:
 *   [ bench the chosen slot-mate , activate the flagged player into the slot it vacates ]
 *
 * Submitted through the EXISTING plan path (POST /execute-plan). No new endpoint — the backend
 * proved this exact shape validates clean against the live Deadwood roster.
 */
export function buildActivatePlanSteps({ slotMate, flaggedPlayer }) {
  if (!slotMate || !flaggedPlayer) {
    throw new Error('buildActivatePlanSteps requires both slotMate and flaggedPlayer');
  }
  const vacated = slotMate.slot;
  if (!isEligibleForSlot(vacated, flaggedPlayer.position)) {
    // Refuse to build a plan the backend would reject — and say why, rather than let the owner
    // discover it as an opaque validation failure.
    throw new Error(
      `${flaggedPlayer.name} is not eligible for ${vacated}. Choose a slot-mate whose slot he can occupy.`);
  }
  return [
    {
      operation: 'roster_move',
      league_player_id: slotMate.leaguePlayerId,
      player_id: slotMate.playerId,
      player_name: slotMate.name,
      plan_step_role: 'enabler',
      pre_move_status: 'active',
      pre_move_position: vacated,
      target_status: 'bench',
      target_position: null,
    },
    {
      operation: 'roster_move',
      league_player_id: flaggedPlayer.leaguePlayerId,
      player_id: flaggedPlayer.playerId,
      player_name: flaggedPlayer.name,
      plan_step_role: 'primary',
      pre_move_status: 'dl',
      pre_move_position: null,
      target_status: 'active',
      target_position: vacated,
    },
  ];
}
